import tvgConf from "@tvg/conf";
import { get } from "lodash";
import {
  COOLDOWN_MIN_DURATION_DEFAULT,
  LOCAL_STORAGE_KEY_PREFIX,
  TVG5_COOKIE
} from "./constants";
import {
  Whitelist,
  UnblockerType,
  BetaFeature,
  BetaFeatureStatus,
  DismissOrigin
} from "../types";
import { gtmOpenFeedbackWidget, gtmSubmitFeedbackWidget } from "../gtm";

const URL: string = get(tvgConf().config(), "domain.desktop", "www-qa.tvg.com");
const DOMAIN = URL.substring(URL.indexOf("."));

export const hasBetaStatus = (
  betaFeature: BetaFeature,
  status: BetaFeatureStatus,
  accountNumber: string,
  localStorageValue?: string
) => {
  let value = localStorageValue;

  if (value === undefined) value = readBetaFeatureOptStatus(accountNumber);

  return value.includes(`${betaFeature}-${status}`);
};

/**
 * Receives a string representing minutes and adds it to the current date
 * @param minutes Minutes as string to add to current date
 * @returns Future date object
 */
export const addMinutesToCurrentDate = (minutes: string = "0") => {
  const cooldownMS = (parseInt(minutes, 10) || 0) * 60000;
  const date = new Date().getTime() + cooldownMS;
  return new Date(date);
};

/**
 * Generates a string with the following format: '-{cooldownType}-{timestamp}'.
 * This will be used as a suffix for the beta features stored in local storage
 * to indicate if the feature has been dismissed or unenrolled, and when that happened
 * E.g.: -dismiss-123129741
 * @param cooldownType "unenroll" or "dismiss"
 * @returns string with status and timestamp
 */
export const generateTimestampWithStatus = (
  cooldownType: BetaFeatureStatus,
  cooldownMinutes?: string
) => {
  const timestamp = addMinutesToCurrentDate(cooldownMinutes)
    .getTime()
    .toString();
  return `-${cooldownType}-${timestamp}`;
};

/**
 * Removes all beta features with cooldown status on a given string and trims everything together
 * separated by a comma
 * E.g.: "programPage-dismiss, myStable" would return "myStable"
 * @param localStorageValue string coming from localStorage
 * @param betaFeature BetaFeature enum
 * @returns string clear of all features with status
 */
export const getClearBetaFromAllStatus = (
  localStorageValue: string,
  betaFeature: BetaFeature
) =>
  localStorageValue
    .split(",")
    .filter(
      (item) =>
        !(
          item.includes(betaFeature) &&
          (item.includes(BetaFeatureStatus.UNENROLL) ||
            item.includes(BetaFeatureStatus.ENROLL) ||
            item.includes(BetaFeatureStatus.DISMISS) ||
            item.includes(BetaFeatureStatus.WELCOMED))
        )
    )
    .join(",");

/**
 * Removes all beta features with a certain cooldown status on a given string and trims everything together
 * separated by a comma
 * @param localStorageValue string coming from localStorage
 * @param betaFeature BetaFeature enum
 * @param cooldownType BetaFeatureStatus enum
 * @returns string clear of all features with status
 */
export const getClearBetaFromStatus = (
  localStorageValue: string,
  betaFeature: BetaFeature,
  cooldownType: BetaFeatureStatus
) =>
  localStorageValue
    .split(",")
    .filter(
      (item) => !(item.includes(betaFeature) && item.includes(cooldownType))
    )
    .join(",");

/**
 * Gets an entry from the localStorage that contains a prefix + the user account number or
 * if it doesnt exist, an emtpy string
 * @param accountNumber User account number
 * @returns value from given key or empty string
 */
export const readBetaFeatureOptStatus = (accountNumber: string) =>
  localStorage.getItem(`${LOCAL_STORAGE_KEY_PREFIX}${accountNumber}`) || "";

export const getParsedBetaInfoFromStorage = (accountNumber: string) =>
  readBetaFeatureOptStatus(accountNumber).split(",").filter(Boolean);

/**
 *
 * @param betaFeature Searches in localStorage for a beta feature
 * @param accountNumber User account number
 * @returns Returns the beta feature or undefined
 */
export const getBetaFeatureFromStorage = (
  betaFeature: BetaFeature,
  accountNumber: string
) =>
  getParsedBetaInfoFromStorage(accountNumber).find((item) =>
    item.includes(betaFeature)
  ) || "";

/**
 * Sets an entry on the localStorage with a prefix + the user account number as key
 * and a given value
 * @param accountNumber User account number
 * @param value string
 */
export const setBetaFeatureOptStatus = (
  accountNumber: string,
  value: string
) => {
  localStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}${accountNumber}`, value);
};

/**
 * Gets an entry from the localStorage for the given account number and clears every status from it
 * to then set it again in the localStorage
 * @param betaFeature BetaFeature enum
 * @param accountNumber User account number
 */
export const clearAllStatusFromFeature = (
  betaFeature: BetaFeature,
  accountNumber: string
) => {
  const localStorageCurrentValue = readBetaFeatureOptStatus(accountNumber);

  const updatedStorage = getClearBetaFromAllStatus(
    localStorageCurrentValue,
    betaFeature
  );

  setBetaFeatureOptStatus(accountNumber, updatedStorage);

  return updatedStorage;
};

/**
 * Gets an entry from the localStorage for the given account number and clears a given status from it
 * to then set it again in the localStorage
 * @param betaFeature BetaFeature enum
 * @param accountNumber User account number
 * @param cooldownType BetaFeatureStatus enum
 */
export const clearStatusFromFeature = (
  betaFeature: BetaFeature,
  accountNumber: string,
  cooldownType: BetaFeatureStatus
) => {
  const localStorageCurrentValue = readBetaFeatureOptStatus(accountNumber);
  const updatedStorage = getClearBetaFromStatus(
    localStorageCurrentValue,
    betaFeature,
    cooldownType
  );
  setBetaFeatureOptStatus(accountNumber, updatedStorage);

  return updatedStorage;
};

/**
 * Gets the entry on localStorage for a given account number, clears all status from it
 * and if something is returned, adds the given beta feature to the returned string
 * and sets it in the localStorage or else it just sets the beta feature in the storage.
 * This will be used to enroll a user in a certain beta feature
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 */
export const saveBetaFeatureOptInStatus = (
  accountNumber: string,
  betaFeature: BetaFeature
) => {
  const localStorageCurrentValue = readBetaFeatureOptStatus(accountNumber);

  const timestamp = generateTimestampWithStatus(BetaFeatureStatus.ENROLL);

  const updatedStorage = getClearBetaFromAllStatus(
    localStorageCurrentValue,
    betaFeature
  );

  const feature = `${betaFeature}${timestamp}`;
  let value = feature;
  const needsReplacement = updatedStorage
    .split(",")
    .find((item) => item.includes(betaFeature));

  if (needsReplacement) {
    value = updatedStorage.replace(needsReplacement, feature);
  } else if (updatedStorage !== "" && !needsReplacement) {
    value = `${updatedStorage},${feature}`;
  }

  setBetaFeatureOptStatus(accountNumber, value);

  return value;
};

/**
 * Gets the entry on localStorage for a given account number and appends the dismiss
 * status to it, then sets it in the storage. This will be used when a user dismisses
 * the modal of the beta program and will allow us to flag the modal as dismissed
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 * @param whitelist Whitelist from CAPI
 */
export const saveDismissStatus = (
  accountNumber: string,
  betaFeature: BetaFeature,
  whitelist: Whitelist[],
  origin: DismissOrigin
) => {
  const localStorageCurrentValue = clearStatusFromFeature(
    betaFeature,
    accountNumber,
    BetaFeatureStatus.DISMISS
  );

  const config = getBetaFeatureConfig(whitelist, betaFeature);

  const cooldownDuration =
    (origin === DismissOrigin.CROSS
      ? config?.dismissCooldownMinutes
      : config?.maybeLaterCooldownMinutes) ?? COOLDOWN_MIN_DURATION_DEFAULT;

  const betaFeatureDismiss =
    betaFeature +
    generateTimestampWithStatus(BetaFeatureStatus.DISMISS, cooldownDuration);

  let value = betaFeatureDismiss;

  if (localStorageCurrentValue !== "") {
    value = `${localStorageCurrentValue},${betaFeatureDismiss}`;
  }

  setBetaFeatureOptStatus(accountNumber, value);

  return value;
};

/**
 * Gets the entry on localStorage for a given account number and appends the "welcomed"
 * status to it, then sets it in the storage. This will be set when the user closes the
 * "Welcome Modal" (modal that is shown when the user lands on a beta feature) to avoid
 * triggering it again and bothering the user. The modal should only be triggered again
 * if the user unenrolls and enrolls again
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 */
export const saveWelcomedStatus = (
  accountNumber: string,
  betaFeature: BetaFeature
) => {
  const localStorageCurrentValue = readBetaFeatureOptStatus(accountNumber);
  const betaFeatureWelcomed = `${betaFeature}-${BetaFeatureStatus.WELCOMED}`;
  const value = localStorageCurrentValue
    ? `${localStorageCurrentValue},${betaFeatureWelcomed}`
    : betaFeatureWelcomed;

  setBetaFeatureOptStatus(accountNumber, value);

  return value;
};

/**
 * Gets the entry on localStorage for a given account number and if it exists it will
 * replace the existant value for the beta feature with the unenrolled status and the current
 * date timestamp.
 * This will be used when the user exits the beta program and will flag this feature as unenrolled
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 * @param whitelist Whitelist from CAPI
 */
export const saveBetaFeatureOptOutStatus = (
  accountNumber: string,
  betaFeature: BetaFeature,
  whitelist: Whitelist[]
) => {
  const updatedStorage = clearAllStatusFromFeature(betaFeature, accountNumber);

  const config = getBetaFeatureConfig(whitelist, betaFeature);

  const unenrolledValue = `${betaFeature}${generateTimestampWithStatus(
    BetaFeatureStatus.UNENROLL,
    config?.unenrollCooldownMinutes ?? COOLDOWN_MIN_DURATION_DEFAULT
  )}`;

  const value =
    !updatedStorage || updatedStorage === ""
      ? unenrolledValue
      : `${updatedStorage},${unenrolledValue}`;

  setBetaFeatureOptStatus(accountNumber, value);

  return value;
};

/**
 * Gets the entry on localStorage for a given account number, splits it and tries to find
 * a given beta feature
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 * @returns boolean
 */
export const isOptedInBetaFeature = (
  accountNumber: string,
  betaFeature: BetaFeature
) =>
  !!getParsedBetaInfoFromStorage(accountNumber).find((item) =>
    item.includes(`${betaFeature}-${BetaFeatureStatus.ENROLL}`)
  );

export const isOptedIn = (accountNumber: string) =>
  !!hasBetaStatusInStorage(BetaFeatureStatus.ENROLL, accountNumber);

/**
 * Searches the config in the whitelist for a given beta feature
 * @param whiteList Features whitelist
 * @param betaFeature BetaFeature enum
 * @returns whitelisted feature or null
 */
export const getBetaFeatureConfig = (
  whiteList: Whitelist[],
  betaFeature: BetaFeature
) => {
  if (whiteList && whiteList.length > 0) {
    return whiteList.find((bf) => bf.id === betaFeature) ?? null;
  }
  return null;
};

// Run the logic to either update a toggle or to set a cookie
// "Toggles" and "cookies" are the methods we currently support to unblock beta features
export const unblockBetaFeature = (
  whiteList: Whitelist[],
  betaFeature: BetaFeature,
  redirectUrl?: string
) => {
  const betaFeatureConfig = getBetaFeatureConfig(whiteList, betaFeature);
  if (betaFeatureConfig) {
    if (betaFeatureConfig.unblockerType === UnblockerType.TOGGLE) {
      // eslint-disable-next-line
      console.log(
        "To-do: implement logic to update (turn on) the toggle stored on our Redux Store"
      );
    } else {
      document.cookie = `${betaFeatureConfig.unblocker};domain=${DOMAIN}; path=/;`;

      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        window.location.reload();
      }
    }
  }
};
// If the user opt out we need the access to the beta feature should be blocked
export const blockBetaFeature = (
  whiteList: Whitelist[],
  betaFeature: BetaFeature
) => {
  const betaFeatureConfig = getBetaFeatureConfig(whiteList, betaFeature);
  if (betaFeatureConfig) {
    if (betaFeatureConfig.unblockerType === UnblockerType.TOGGLE) {
      // eslint-disable-next-line
      console.log(
        "To-do: implement logic to update (turn off) the toggle stored on our Redux Store"
      );
    } else {
      // Omit cookie value and set the expire parameter to a past date
      document.cookie = `${
        betaFeatureConfig.unblocker.split("=")[0]
      }=;domain=${DOMAIN};path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC`;
      window.location.reload();
    }
  }
};

/**
 * Searches in a whitelist feature if a user is listed for it
 * @param accountNumber User account nuber
 * @param whiteList Features whitelist
 * @param betaFeature BetaFeature enum
 * @returns boolean
 */
export const isEligible = (
  accountNumber: string,
  whiteList: Whitelist[],
  betaFeature: BetaFeature
) => {
  const whitelistedFeature = getBetaFeatureConfig(whiteList, betaFeature);

  if (whitelistedFeature) {
    return whitelistedFeature.usersWhitelist.includes(accountNumber);
  }
  return false;
};

/**
 * Searches in a whitelist if a user is invited for any feature
 * @param accountNumber User account nuber
 * @param whitelist Features whitelist
 * @returns boolean
 */
export const isEligibleForAny = (
  accountNumber: string,
  whitelist: Whitelist[]
) => !!whitelist.find((list) => list.usersWhitelist.includes(accountNumber));

/**
 * Executes the logic necessary to enroll a user in a beta feature.
 * @param accountNumber User account number
 * @param whitelist Features whitelist
 * @param betaFeature BetaFeature enum
 */
export const enrollInBeta = (
  accountNumber: string,
  whitelist: Whitelist[],
  betaFeature: BetaFeature,
  redirectUrl?: string
) => {
  saveBetaFeatureOptInStatus(accountNumber, betaFeature);
  unblockBetaFeature(whitelist, betaFeature, redirectUrl);
};

export const unenrollInBeta = (
  accountNumber: string,
  whitelist: Whitelist[],
  betaFeature: BetaFeature
) => {
  saveBetaFeatureOptOutStatus(accountNumber, betaFeature, whitelist);
  blockBetaFeature(whitelist, betaFeature);
};

/**
 * Calculate the minutes between the current timestamp and the one in item passed as arg
 * @param item string coming from the localstorage which needs to have a timestamp
 * @returns boolean
 */
export const isBetaValueInCooldown = (item: string) => {
  const currDate = new Date().getTime(); // Current timestamp
  const timestamp = item.match(/\d+/)?.toString() || "0"; // Searches for a number in the localStorage string
  const parsedTimestamp = parseInt(timestamp, 10); // Time in milliseconds since the timestamp was created
  const cooldownDate = new Date(parsedTimestamp).getTime();

  return cooldownDate > currDate;
};

/**
 * Gets the entry on localStorage for a given account number and if it exists and has
 * the given beta feature, we will search for the same beta feature which has the given
 * cooldown status.
 * And finally calculate the minutes between the current timestamp and the one in the storage.
 * This will be used to know if a feature was dismissed/exited recently and if we want to
 * open the modal again.
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 * @param cooldownType BetaFeatureStatus enum
 * @returns booean
 */
export const isFeatureInCooldown = (
  accountNumber: string,
  betaFeature: BetaFeature,
  cooldownType: BetaFeatureStatus
) => {
  const localStorageCurrentValue = readBetaFeatureOptStatus(accountNumber);

  // Verifies if the string has the beta feature we are searching for
  if (
    localStorageCurrentValue &&
    localStorageCurrentValue.includes(betaFeature)
  ) {
    // Search for the beta feature that is exit or dismiss
    const foundBetaFeature = localStorageCurrentValue
      .split(",")
      .find(
        (item) => item.includes(betaFeature) && item.includes(cooldownType)
      );

    if (foundBetaFeature === undefined) return false;

    return isBetaValueInCooldown(foundBetaFeature);
  }
  return false;
};

/**
 * Gets the entry on localStorage for a given account number and if it exists,
 * we will search for the a cooldown status.
 * And finally calculate the minutes between the current timestamp and the one in the storage.
 * This will be used to know if a feature was dismissed/exited recently and if we want to
 * open the modal again.
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 * @param whitelist Features whitelist
 * @param cooldownType BetaFeatureStatus enum
 * @returns booean
 */
export const isAnyFeatureInCooldown = (
  accountNumber: string,
  cooldownType: BetaFeatureStatus
) => {
  const localStorageCurrentValue = getParsedBetaInfoFromStorage(accountNumber);

  // Verifies if the string has the beta feature we are searching for
  if (localStorageCurrentValue.length > 0) {
    // Search for the beta feature that is exit or dismiss
    const foundBetaItem = hasBetaStatusInStorage(cooldownType, accountNumber);

    if (foundBetaItem === undefined) return false;

    return isBetaValueInCooldown(foundBetaItem);
  }
  return false;
};

/**
 * Gets the entry on localStorage for a given account number and checks for the existance
 * of a given beta feature with the dismiss status
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 * @returns boolean
 */
export const hasDismissedBetaFeatureEnroll = (
  accountNumber: string,
  betaFeature: BetaFeature
) => hasBetaStatus(betaFeature, BetaFeatureStatus.DISMISS, accountNumber);

export const dispatchSubmited = () => {
  gtmSubmitFeedbackWidget();
  document.removeEventListener("apptentive:survey:submit", dispatchSubmited);
};

export const trackBetaFeatureFeedback = () => {
  gtmOpenFeedbackWidget();
  document.addEventListener("apptentive:survey:submit", dispatchSubmited);
};

/**
 * Loops through the whitelist and returns all features allowed to a user
 * @param accountNumber User account number
 * @param whitelist Features whitelist
 * @returns
 */
export const getUserAllowedFeatures = (
  accountNumber: string,
  whitelist: Whitelist[]
) => whitelist.filter((item) => item.usersWhitelist.includes(accountNumber));

/*
 * Gets the entry on localStorage for a given account number and checks for the existance
 * of a given beta feature with the welcomed status
 * @param accountNumber User account number
 * @param betaFeature BetaFeature enum
 * @returns boolean
 */
export const hasBeenWelcomedOnBetaFeature = (
  accountNumber: string,
  betaFeature: BetaFeature
) => hasBetaStatus(betaFeature, BetaFeatureStatus.WELCOMED, accountNumber);

export const hasBetaStatusInStorage = (
  status: BetaFeatureStatus,
  accountNumber: string
) =>
  getParsedBetaInfoFromStorage(accountNumber).find((item) => {
    // eslint-disable-next-line security/detect-non-literal-regexp
    const regex = new RegExp(`\\w+-${status}(-\\d+)?`, "g");
    return item.match(regex);
  });

export const areNewFeaturesAvailable = (
  accountNumber: string,
  whitelist: Whitelist[]
) => {
  const whitelistFeatures = getUserAllowedFeatures(
    accountNumber,
    whitelist
  ).map((item) => item.id);

  return whitelistFeatures.reduce((acc, feature) => {
    const storage = readBetaFeatureOptStatus(accountNumber);
    const isFeatureStored = storage.includes(feature);

    const updatedStorage = storage === "" ? feature : `${storage},${feature}`;

    if (!isFeatureStored) {
      setBetaFeatureOptStatus(accountNumber, updatedStorage);
    }

    return acc || !isFeatureStored;
  }, false);
};

export const shouldRenderNewTag = (accountNumber: string) =>
  readBetaFeatureOptStatus(accountNumber) === "" ||
  !!(
    hasBetaStatusInStorage(BetaFeatureStatus.DISMISS, accountNumber) &&
    !hasBetaStatusInStorage(BetaFeatureStatus.UNENROLL, accountNumber) &&
    !hasBetaStatusInStorage(BetaFeatureStatus.ENROLL, accountNumber)
  );

export const syncStorageAndCookie = (accountNumber: string) => {
  // If there is no cookie, we clean the storage
  if (
    !document.cookie.includes(`${TVG5_COOKIE}=true`) &&
    !!hasBetaStatusInStorage(BetaFeatureStatus.ENROLL, accountNumber)
  ) {
    clearStatusFromFeature(
      BetaFeature.PROGRAM_PAGE,
      accountNumber,
      BetaFeatureStatus.ENROLL
    );
  }
};

const bannedPages = ["responsible-gaming"];
export const isBannedPagePath = (pagePath: string) => {
  const normalizedPath = pagePath.replace(/\//g, "");
  return bannedPages.includes(normalizedPath);
};
